import { ChevronDown, PlugZap } from 'lucide-react'
import { GoogleDriveIcon } from '@/components/icons/google-drive-icon'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS } from '@/lib/menu-chrome'
import { testIds } from '@/lib/test-ids'
import { isDriveFileImportable, driveImportBlockReason } from '@/lib/drive-importability'
import { workspaceBadgeFromPath } from '@/lib/workspace-path-match'
import { fetchWithAuth } from '@/lib/api'
import type { Workspace } from '@/providers/workspace-provider'
import type { CommandItem } from './command-palette.types'
import { getDocumentIcon } from './command-palette.utils'

type DriveResolveResponse = {
  file?: { id: string; name: string; mimeType: string; isFolder?: boolean }
  existingDocument?: {
    id: string
    title?: string
    filePath?: string
    organizationId?: string
  }
  googleAccess?: boolean
  error?: string
}

function workspaceForOrgId(
  organizationId: string | undefined,
  workspaces: Workspace[],
): Workspace | undefined {
  if (!organizationId) return undefined
  return workspaces.find((ws) => ws.id === organizationId)
}

export async function buildGoogleDriveCommandRows(opts: {
  url: string
  fileId: string
  organizationId?: string
  workspaces: Workspace[]
  activeWorkspaceIds: string[]
  auth: typeof fetchWithAuth
  signal?: AbortSignal
}): Promise<CommandItem[]> {
  const { url, fileId, organizationId, workspaces, activeWorkspaceIds, auth, signal } = opts

  let res: Response
  try {
    res = await auth('/api/google-drive/resolve-url', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url, organizationId }),
      signal,
    })
  } catch (err) {
    if (signal?.aborted || (err instanceof DOMException && err.name === 'AbortError')) {
      return []
    }
    return [
      {
        id: `drive-resolve-error-${fileId}`,
        title: 'Could not check Google Drive',
        type: 'action',
        icon: <GoogleDriveIcon className="h-4 w-4" />,
        subtitle: err instanceof Error ? err.message : 'Resolve failed',
        action: 'open-google-drive-sidebar',
        matchScore: 200,
        driveFileId: fileId,
        driveSourceUrl: url,
      },
    ]
  }

  if (signal?.aborted) return []

  if (res.status === 401) {
    return [
      {
        id: `connect-google-drive-${fileId}`,
        title: 'Connect Google Drive to import',
        type: 'action',
        icon: <PlugZap className="h-4 w-4" />,
        subtitle: 'Open the Drive sidebar and connect your Google account',
        action: 'open-google-drive-sidebar',
        matchScore: 250,
        matchType: 'google-drive-url',
        driveFileId: fileId,
        driveSourceUrl: url,
      },
    ]
  }

  const body = (await res.json().catch(() => ({}))) as DriveResolveResponse
  if (!res.ok) {
    return [
      {
        id: `drive-resolve-error-${fileId}`,
        title: 'Google Drive file not available',
        type: 'action',
        icon: <GoogleDriveIcon className="h-4 w-4" />,
        subtitle: typeof body.error === 'string' ? body.error : `HTTP ${res.status}`,
        action: 'open-google-drive-sidebar',
        matchScore: 200,
        matchType: 'google-drive-url',
        driveFileId: fileId,
        driveSourceUrl: url,
      },
    ]
  }

  const existing = body.existingDocument
  if (existing?.id) {
    const ws = workspaceForOrgId(existing.organizationId, workspaces)
    const workspaceActive =
      !ws?.id || activeWorkspaceIds.length === 0 || activeWorkspaceIds.includes(ws.id)
    const subtitle = existing.filePath
      || (body.googleAccess === false
        ? 'Linked in Maater (connected Google account cannot open this Drive file)'
        : 'Already linked in Maater')

    if (ws?.id && !workspaceActive) {
      return [
        {
          id: `enable-workspace-open-${ws.id}-${existing.id}`,
          title: `Enable ${ws.name} to open`,
          type: 'action',
          icon: <GoogleDriveIcon className="h-4 w-4" />,
          subtitle,
          action: 'enable-workspace-and-open',
          matchScore: 250,
          matchType: 'google-drive-url',
          filePath: existing.filePath,
          enableWorkspaceId: ws.id,
          enableWorkspaceName: ws.name,
          existingDocId: existing.id,
          driveFileId: fileId,
          driveSourceUrl: url,
        },
      ]
    }

    const badge = existing.filePath ? workspaceBadgeFromPath(existing.filePath, workspaces) : null
    return [
      {
        id: existing.id,
        title: `Open ${existing.title || 'linked Google Doc'}`,
        type: 'file',
        icon: getDocumentIcon('note'),
        subtitle,
        action: 'open-document',
        matchScore: 250,
        matchType: 'google-drive-url',
        filePath: existing.filePath,
        existingDocId: existing.id,
        driveFileId: fileId,
        driveSourceUrl: url,
        ...(badge ? { workspaceBadge: badge } : {}),
      },
    ]
  }

  const file = body.file
  const mimeType = file?.mimeType || 'application/vnd.google-apps.document'
  const importable = isDriveFileImportable(mimeType)
  const name = file?.name || 'Google Doc'

  if (!importable) {
    const reason = driveImportBlockReason(mimeType) || 'This Drive file cannot be imported as a Maater document.'
    return [
      {
        id: `drive-not-importable-${fileId}`,
        title: name,
        type: 'action',
        icon: <GoogleDriveIcon className="h-4 w-4" />,
        subtitle: reason,
        action: 'open-google-drive-sidebar',
        matchScore: 220,
        matchType: 'google-drive-url',
        driveFileId: fileId,
        driveSourceUrl: url,
      },
    ]
  }

  return [
    {
      id: `import-google-drive-${fileId}`,
      title: `Import ${name}`,
      type: 'action',
      icon: <GoogleDriveIcon className="h-4 w-4" />,
      subtitle: 'Import to current folder',
      action: 'import-google-drive',
      matchScore: 250,
      matchType: 'google-drive-url',
      driveFileId: fileId,
      driveSourceUrl: url,
      driveFileName: name,
      driveMimeType: mimeType,
    },
  ]
}

/** Inline Import-to-folder chrome for a Drive URL row in the command palette. */
export function GoogleDriveImportRowChrome({
  folderLabel,
  onImport,
  onChooseFolder,
}: {
  folderLabel: string
  onImport: () => void
  onChooseFolder: () => void
}) {
  return (
    <div
      className="flex shrink-0 items-center gap-1"
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <Button
        type="button"
        size="sm"
        variant="secondary"
        tabIndex={-1}
        className="h-7 px-2 text-xs"
        data-testid={testIds.commandPalette.driveImportToFolder}
        onClick={onImport}
      >
        Import to {folderLabel}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            tabIndex={-1}
            className="h-7 w-7"
            aria-label="Choose import folder"
            data-testid={testIds.commandPalette.driveChooseFolder}
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className={QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS}>
          <DropdownMenuItem onClick={onImport}>Import to {folderLabel}</DropdownMenuItem>
          <DropdownMenuItem onClick={onChooseFolder}>Choose folder...</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
